import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FiUser, FiCreditCard, FiHome, FiLogOut, FiLink, FiEye, FiEyeOff,
  FiDownload, FiHelpCircle, FiUsers,
} from "react-icons/fi";
import { CachedAvatar } from "@/shared/components/ui/cached-avatar";
import { CurrentUserProfile } from "./CurrentUserProfile";
import { useAuth } from "@/contexts/AuthContext";
import { usePrivacy } from "@/contexts/PrivacyContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useApp } from "@/contexts/AppContext";
import { usePWAInstall } from "@/pwa/usePWAInstall";

type SavedSession = ReturnType<typeof useAuth>['savedSessions'][number];

/**
 * Sessões salvas do menu de conta (exclui a sessão atual) + troca/remoção.
 * Compartilhado entre o flyout desktop (UserProfileDropdown) e a sub-tela
 * do drawer mobile (Sidebar) — evita duplicar o handler de troca.
 */
export const useAccountSessions = () => {
  const navigate = useNavigate();
  const { state, savedSessions, loginWithSavedSession, removeSession } = useAuth();
  const currentEmail = state.user?.email || "";
  const otherSessions = savedSessions.filter((s) => s.email !== currentEmail);

  const switchTo = async (session: SavedSession) => {
    try {
      await loginWithSavedSession(session);
      // Cache do React Query é limpo automaticamente em loginWithSavedSession()
      navigate('/');
    } catch (error) {
      console.error('Error switching session:', error);
    }
  };

  return { otherSessions, switchTo, removeSession };
};

export type AccountMenuVariant = 'flyout' | 'drawer';

export interface AccountMenuContentProps {
  variant: AccountMenuVariant;
  /** Fecha a superfície dona do menu (flyout desktop / drawer mobile) */
  onClose: () => void;
  /** Abre o AllSessionsDialog (estado vive no dono da superfície) */
  onOpenAllSessions: () => void;
  /** Abre o CompanyEditDrawer (estado vive no dono da superfície) */
  onOpenCompanyDrawer: () => void;
}

interface AccountMenuItem {
  id: string;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  run: () => void;
  visible: boolean;
  danger?: boolean;
}

/**
 * Conteúdo do menu "Minha conta" — componente plano (sem Radix), renderizado
 * pelo flyout desktop (variant 'flyout', paleta light fixa dos dropdowns) e
 * pela sub-tela do drawer mobile (variant 'drawer', tokens da sidebar com a
 * anatomia dos itens do drawer: 46px / fontSize 15 / ícone 20px).
 */
export const AccountMenuContent: React.FC<AccountMenuContentProps> = ({
  variant,
  onClose,
  onOpenAllSessions,
  onOpenCompanyDrawer,
}) => {
  const navigate = useNavigate();
  const { state, logout } = useAuth();
  const { isPrivacyMode, togglePrivacyMode } = usePrivacy();
  const { canManageCompany, canAccessPath } = usePermissions();
  const { app } = useApp();
  const { canInstall, promptInstall } = usePWAInstall();
  const { otherSessions, switchTo } = useAccountSessions();

  const hasSupportWidget = !!(app?.support?.widget && app?.widgetUrl);
  const isDrawer = variant === 'drawer';

  const user = state.user;
  const displayName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : "Usuário";
  const displayEmail = user?.email || "";

  const goTo = (path: string) => {
    onClose();
    navigate(path);
  };

  const handleTogglePrivacy = () => {
    togglePrivacyMode();
    toast.success(isPrivacyMode ? 'Modo privacidade desativado' : 'Modo privacidade ativado');
    onClose();
  };

  const items: AccountMenuItem[] = [
    { id: 'account', icon: FiUser, label: 'Minha conta', run: () => goTo('/settings/account'), visible: true },
    // Visibilidade = poder abrir a tela (lib/route-access.ts). "Integração" é a
    // tela de chaves de API: exige `api-keys:read`, não `company:read` (antes
    // exigia Empresa e escondia o menu de 43 pessoas com API Keys liberado).
    { id: 'integration', icon: FiLink, label: 'Integração', run: () => goTo('/integration/api-keys'), visible: canAccessPath('/integration/api-keys') },
    { id: 'billing', icon: FiCreditCard, label: 'Cobrança', run: () => goTo('/billing'), visible: canAccessPath('/billing') },
    { id: 'company', icon: FiHome, label: 'Empresa', run: () => { onClose(); onOpenCompanyDrawer(); }, visible: canManageCompany() },
    { id: 'install', icon: FiDownload, label: 'Instalar App', run: () => { onClose(); promptInstall(); }, visible: canInstall },
    { id: 'help', icon: FiHelpCircle, label: 'Ajuda', run: () => { onClose(); window.TroiaWidget?.open(); }, visible: hasSupportWidget },
    { id: 'privacy', icon: isPrivacyMode ? FiEyeOff : FiEye, label: 'Modo privacidade', run: handleTogglePrivacy, visible: true },
  ];

  // ---- estilos por variant ----
  const itemBase = isDrawer
    ? 'flex items-center w-full text-left'
    : 'w-full flex items-center rounded-md transition-all duration-150 text-left p-2 gap-3 text-sm font-medium cursor-pointer';
  const itemStyle = (danger?: boolean): React.CSSProperties =>
    isDrawer
      ? {
          height: 46,
          padding: '0 12px',
          gap: 13,
          borderRadius: 10,
          fontSize: 15,
          fontWeight: 500,
          color: danger ? 'var(--theme-error-color)' : 'var(--theme-sidebar-text)',
          backgroundColor: 'transparent',
        }
      : { color: danger ? 'var(--theme-error-color)' : '#374151' };
  const iconClass = isDrawer ? 'h-[20px] w-[20px] flex-shrink-0' : 'h-4 w-4 flex-shrink-0';
  const iconStyle = (danger?: boolean): React.CSSProperties => ({
    color: danger
      ? 'var(--theme-error-color)'
      : isDrawer ? 'var(--theme-muted-text)' : '#64748b',
  });
  const handleMouseEnter = (danger?: boolean) => (e: React.MouseEvent<HTMLElement>) => {
    if (isDrawer) return;
    e.currentTarget.style.backgroundColor = '#f1f5f9';
    if (!danger) e.currentTarget.style.color = '#1e293b';
  };
  const handleMouseLeave = (danger?: boolean) => (e: React.MouseEvent<HTMLElement>) => {
    if (isDrawer) return;
    e.currentTarget.style.backgroundColor = 'transparent';
    if (!danger) e.currentTarget.style.color = '#374151';
  };
  const separator = (
    <div
      className="my-1 h-px"
      style={{ backgroundColor: isDrawer ? 'var(--theme-sidebar-border)' : '#e5e7eb' }}
    />
  );

  return (
    <div data-testid={`account-menu-${variant}`}>
      {/* Header: perfil atual */}
      {isDrawer ? (
        <div className="flex items-center gap-3" style={{ padding: '10px 12px' }}>
          <CachedAvatar src={user?.avatar} alt={displayName} size="lg" isSquircle={true} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm truncate" style={{ color: 'var(--theme-sidebar-text-hover)' }}>
              {displayName}
            </p>
            <p className="text-xs truncate opacity-75" style={{ color: 'var(--theme-sidebar-text)' }}>
              {displayEmail}
            </p>
          </div>
        </div>
      ) : (
        <CurrentUserProfile avatarUrl={user?.avatar} displayName={displayName} displayEmail={displayEmail} />
      )}

      {separator}

      {/* Trocar de conta */}
      {otherSessions.length > 0 && (
        <>
          <p
            className="px-3 py-2 mb-1"
            style={{
              color: isDrawer ? 'var(--theme-muted-text)' : '#64748b',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase' as const,
            }}
          >
            Trocar de conta
          </p>
          <div className="space-y-1">
            {otherSessions.slice(0, 2).map((session) => (
              <button
                key={session.id}
                type="button"
                data-testid={`account-menu-session-${session.id}`}
                onClick={() => { onClose(); void switchTo(session); }}
                className={
                  isDrawer
                    ? 'w-full flex items-center gap-2 rounded-[10px] text-left'
                    : 'w-full flex items-center gap-2 p-2 rounded-md transition-all duration-150 text-left'
                }
                style={isDrawer ? { padding: '6px 12px', color: 'var(--theme-sidebar-text)' } : { color: '#374151' }}
                onMouseEnter={handleMouseEnter()}
                onMouseLeave={handleMouseLeave()}
              >
                <CachedAvatar src={session.avatar} alt={session.name} size="sm" isSquircle={true} />
                <div className="flex-1 overflow-hidden">
                  <p className="text-sm font-medium truncate" style={{ color: 'inherit' }}>
                    {session.name}
                  </p>
                  <p className="text-xs truncate" style={{ color: isDrawer ? 'var(--theme-muted-text)' : '#64748b' }}>
                    {session.email}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {otherSessions.length > 2 && (
            <button
              type="button"
              data-testid="account-menu-manage-sessions"
              onClick={() => { onClose(); onOpenAllSessions(); }}
              className={
                isDrawer
                  ? 'w-full flex items-center gap-2 rounded-[10px] text-left text-sm font-medium'
                  : 'w-full flex items-center gap-2 p-2 rounded-md transition-all duration-150 text-sm font-medium'
              }
              style={isDrawer ? { padding: '8px 12px', color: 'var(--theme-sidebar-text)' } : { color: '#374151' }}
              onMouseEnter={handleMouseEnter()}
              onMouseLeave={handleMouseLeave()}
            >
              <FiUsers className="h-4 w-4" style={{ color: isDrawer ? 'var(--theme-muted-text)' : '#64748b' }} />
              <span>Gerenciar contas ({otherSessions.length})</span>
            </button>
          )}

          {separator}
        </>
      )}

      {/* Itens */}
      {items.filter((item) => item.visible).map((item) => (
        <button
          key={item.id}
          type="button"
          data-testid={`account-menu-item-${item.id}`}
          onClick={item.run}
          className={itemBase}
          style={itemStyle()}
          onMouseEnter={handleMouseEnter()}
          onMouseLeave={handleMouseLeave()}
        >
          <item.icon className={iconClass} style={iconStyle()} />
          {item.label}
        </button>
      ))}

      {separator}

      {/* Sair */}
      <button
        type="button"
        data-testid="account-menu-logout"
        onClick={() => { onClose(); logout(); }}
        className={itemBase}
        style={itemStyle(true)}
        onMouseEnter={handleMouseEnter(true)}
        onMouseLeave={handleMouseLeave(true)}
      >
        <FiLogOut className={iconClass} style={iconStyle(true)} />
        Sair da conta
      </button>
    </div>
  );
};
